import React, { useEffect, useImperativeHandle, useState, useRef } from 'react'
import { Dialog } from '@headlessui/react'
import CloseButton from '@/component/button/CloseButton.tsx'

// import CloseIcon from '@/public/svg/ic_close.svg'

export interface IProps {
    title?: string
    renderTitleRight?: React.ReactNode
    description?: string
    onCloseCallback?: () => void
    onOpenCallback?: () => void
    disableOutSideClickClose?: boolean
    children?: React.ReactNode
    width?: ModalWidthType
    showCloseButton?: boolean
    panelClassName?: string
}

export type ModalWidthType = '400' | '600' | '800' | '900' | '1200'

interface IModalStyle {
    widthClassName?: string
}

const styleMap = new Map<ModalWidthType, IModalStyle>()
styleMap.set('400', { widthClassName: 'max-w-[400px] w-full' })
styleMap.set('600', { widthClassName: 'max-w-[600px] w-full' })
styleMap.set('800', { widthClassName: 'max-w-[800px] w-full' })
styleMap.set('900', { widthClassName: 'max-w-[900px] w-full' })
styleMap.set('1200', { widthClassName: 'max-w-[1200px] w-full' })

const BaseModal = (
    {
        children,
        title,
        width = '600',
        renderTitleRight,
        onCloseCallback,
        onOpenCallback,
        disableOutSideClickClose,
        showCloseButton = true,
        panelClassName
    }: IProps,
    ref: any
) => {
    const [isOpen, setIsOpen] = useState(false)
    const closeButtonRef = useRef<HTMLButtonElement>(null)

    useImperativeHandle(ref, () => ({
        show: () => {
            showModal()
        },
        close: () => {
            closeModal()
        }
    }))

    const closeModal = () => {
        setIsOpen(false)
        onCloseCallback && onCloseCallback()
    }

    const showModal = () => {
        setIsOpen(true)
    }

    useEffect(() => {
        if (isOpen) {
            onOpenCallback && onOpenCallback()
        }
    }, [isOpen])

    return (
        <Dialog
            open={isOpen}
            onClose={disableOutSideClickClose ? () => null : () => {
                setIsOpen(false)
                onCloseCallback && onCloseCallback()
            }}
            initialFocus={closeButtonRef}
            className={'ui-modal relative z-modal overflow-hidden'}>
            <div className="ui-modal__backdrop fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className={'fixed inset-0 flex w-screen items-center justify-center p-[12px]'}>
                <Dialog.Panel className={`${styleMap.get(width)?.widthClassName} ui-modal__panel relative max-h-[calc(100dvh-24px)] overflow-y-auto rounded-lg bg-white md:w-[calc(100vw-24px)] md:max-w-[calc(100vw-24px)] ${panelClassName || ''}`}>
                    {showCloseButton && !title && !renderTitleRight && (
                        <div
                            ref={closeButtonRef as unknown as React.RefObject<HTMLDivElement>}
                            className={'absolute right-[10px] top-[10px] z-[1]'}
                        >
                            <CloseButton size={'small'} onClick={closeModal} />
                        </div>
                    )}
                    {(title || renderTitleRight) &&
                        <div className={'ui-modal__header flex items-center gap-x-2 justify-between min-h-[58px] px-[18px]'}>
                            <Dialog.Title className={'text-black text-heading1'}>{title}</Dialog.Title>
                            {renderTitleRight}
                            {showCloseButton && <div ref={closeButtonRef as unknown as React.RefObject<HTMLDivElement>}>
                                <CloseButton onClick={closeModal} />
                            </div>}
                        </div>}
                    {children}
                    {!showCloseButton && !title && (
                        <button
                            ref={closeButtonRef}
                            onClick={closeModal}
                            className="sr-only"
                            aria-label="Close modal"
                        >
                            Close
                        </button>
                    )}
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default React.forwardRef(BaseModal)
